import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Modal, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../../Components/Button"
import EmptyComponent from "../../../../Components/Empty"
import Filters from "../../../../Components/Filters"
import Table from "../../../../Components/Table"
import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import { serializeData } from "../../../../Utils/utils"
import { usePricingTabPreferences } from "../../hooks/usePricingTabPreferences"
import { PRICING_TAB } from "../../redux/pricing.constants"
import {
  changeFilters,
  changeVisibleFilters,
  resetFilters,
  selectAlertsPricing,
  selectAlertsPricingFetching,
  selectArePricingFiltersDefault,
  selectPricingFilterOptions,
  selectPricingFilters,
  selectPricingSaveFetching,
  selectPricingVisibleFilters
} from "../../redux/pricing.slice"
import {
  createPricing,
  deletePricing,
  getPricing,
  getPricingFilterOptions,
  updatePricing
} from "../../redux/pricing.thunks"
import { getActionColumn, getColumns, getDefaultColumn } from "./columns"
import PricingForm from "./components/PricingForm"

const TAB = PRICING_TAB.ALERTS

const pageSize = 50

const columnsAdmin = [
  { field_id: "pricing_schema", label: "Pricing scheme", active: true },
  { field_id: "type", label: "Type", active: true },

  { field_id: "ethoca_price", label: "Ethoca", active: true },
  { field_id: "rdr_1_price", label: "RDR", active: true },
  { field_id: "cdrn_price", label: "CDRN", active: true },

  { field_id: "count_of_free_alerts", label: "Number of the first alerts to skip", active: true },
  { field_id: "applied", label: "Applied", active: true }
]

const activeColumns = columnsAdmin.filter(({ active }) => active)

const { Text } = Typography

const AlertsTab = () => {
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const dispatch = useDispatch()

  const isMobile = useSelector((state) => state.nav.isMobile)
  const businessAccountId = useSelector((state) => state.auth.userInfo.current_business_account_id)
  const statistics = useSelector((state) => state.businessAccounts.statistics)

  const { count, results } = useSelector(selectAlertsPricing)
  const fetching = useSelector(selectAlertsPricingFetching)
  const filters = useSelector(selectPricingFilters(TAB))
  const visibleFilters = useSelector(selectPricingVisibleFilters(TAB))
  const filterOptions = useSelector(selectPricingFilterOptions(TAB))
  const isFiltersDefault = useSelector(selectArePricingFiltersDefault(TAB))
  const saveFetching = useSelector(selectPricingSaveFetching)

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [modalType, setModalType] = useState(null)
  const [selectPrice, setSelectPrice] = useState(null)

  const pricingList = results?.map((price) => price?.pricing_schema?.toLowerCase())

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const handleCloseModal = () => {
    setSelectPrice(null)
    setModalType(null)
  }

  const getPricingInfo = () => {
    dispatch(
      getPricing({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        ...serializeData(filters),
        columns: ["id", ...activeColumns.map(({ field_id }) => field_id)].join(",")
      })
    )
  }

  usePricingTabPreferences(TAB)

  useEffect(() => {
    dispatch(getPricingFilterOptions())
  }, [])

  useEffect(() => {
    getPricingInfo()
  }, [businessAccountId, ordering, pageNumber, filters])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: count ? "max-content" : 230 * activeColumns.length,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 56px)",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [count, pageNumber]
  )

  const tableColumns = useMemo(() => {
    const allColumns = getColumns()
    return [
      ...activeColumns.map(
        (data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data, t })
      ),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      dispatch(changeVisibleFilters({ tab: TAB, visibleFilters: newData }))
    } else {
      dispatch(changeFilters({ tab: TAB, filters: newData }))
      setPageNumber(1)
    }
  }, [])

  const handleClearFilters = () => {
    dispatch(resetFilters(TAB))
    setPageNumber(1)
  }

  function handleSelectRow(priceData, type) {
    if (type === "settings") {
      setSelectPrice(priceData)
      setModalType("updatePricing")
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Delete pricing scheme",
        content:
          "Are you sure you want to delete this pricing scheme? This action can NOT be undone and all business accounts using it will be switched to Default pricing scheme automatically.",

        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          dispatch(deletePricing(priceData.id))
        }
      })
    }
  }

  const confirmMinMaxPrice = (onOk) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: "Are you sure you want to save these prices?",
      content:
        "Some prices are outside the standard range. Please confirm if everything is correct before saving.",

      cancelText: t("Cancel"),
      okText: t("Confirm"),
      onOk() {
        onOk()
      }
    })
  }

  const confirmUpdatePrice = (onOk) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: "Saving prices",
      content:
        "Are you sure you want to save the changes? This action can NOT be undone. The new prices will be immediately applied to all business accounts using the current pricing scheme.",

      cancelText: t("Cancel"),
      okText: t("Save"),
      onOk() {
        onOk()
      }
    })
  }

  const savePrice = async (data) => {
    try {
      if (selectPrice) {
        await dispatch(updatePricing({ id: selectPrice.id, ...data })).unwrap()
        notification.success({
          message: "Pricing scheme has been successfully updated.",
          ...notificationConfig
        })
      } else {
        await dispatch(createPricing(data)).unwrap()
        notification.success({
          message: "New pricing scheme has been successfully created.",
          ...notificationConfig
        })
        getPricingInfo()
      }
      handleCloseModal()
    } catch {
      // The thunk has already shown the API error; the modal stays open for a retry.
    }
  }

  const createOrUpdatePrice = (data) => {
    if (selectPrice) {
      confirmUpdatePrice(() => savePrice(data))
    } else {
      savePrice(data)
    }
  }

  const handleSubmitForm = (data) => {
    let finalData

    if (selectPrice && data?.pricing_items) {
      const initialPricingItems = [
        ...(selectPrice?.ethoca_price_levels || []),
        ...(selectPrice?.rdr_1_price_levels || []),
        ...(selectPrice?.cdrn_price_levels || [])
      ]

      const finalPricingItemsWithId = data?.pricing_items
        ?.filter((price) => price?.id)
        .map((price) => price?.id)

      const deletedPricingItems = [...initialPricingItems]
        ?.filter((el) => !finalPricingItemsWithId?.includes(el.id))
        ?.map((el) => ({ ...el, is_delete: true }))

      finalData = {
        ...data,
        pricing_items: [...(data?.pricing_items || []), ...deletedPricingItems]
      }
    } else {
      finalData = { ...data }
    }

    const checkPrices = () => {
      return Object.keys(data).every((key) => {
        if (key.includes("_price")) {
          const value = data[key]
          return value >= 10 && value <= 40
        }

        if (key === "pricing_items") {
          return data[key].every(({ price }) => price >= 10 && price <= 40)
        }

        return true // skip keys that don't contain _price
      })
    }

    if (checkPrices()) {
      createOrUpdatePrice(finalData)
    } else {
      confirmMinMaxPrice(() => createOrUpdatePrice(finalData))
    }
  }

  return (
    <>
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={columnsAdmin}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          withoutAdd={true}
          showClearBtn={!isFiltersDefault}
          onClearFilters={handleClearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <Button
            className="flex center"
            icon={<Icon type="plus" style={{ fontSize: 20 }} />}
            onClick={() => {
              setModalType("newPricing")
            }}
          >
            New pricing scheme
          </Button>
        </Space>
      </Flex>

      <Table
        columns={tableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        locale={{
          emptyText: !statistics?.["pricing_counter"] ? (
            <EmptyComponent type="no_data_available" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={handleClearFilters}
              >
                {t("Clear search")}
              </Button>
            </EmptyComponent>
          )
        }}
      />

      <Modal
        open={!!modalType}
        centered={!isMobile}
        width={442}
        title={"Pricing scheme"}
        footer={null}
        closable="true"
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={{ content: "clear-bg" }}
      >
        <Spin size="large" spinning={saveFetching}>
          <PricingForm
            optionsType={filterOptions?.["type"] || []}
            pricingList={pricingList}
            initialValues={selectPrice}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
          />
        </Spin>
      </Modal>
    </>
  )
}

export default AlertsTab
